import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchQuickTaskThunk } from "@/store/thunks/tasksThunks";

import AWSCognitoContext from "../../contexts/aws-cognito-context";
import { fetchPickList } from "../../store/thunks/pickListThunk";
import { getVoyagesList } from "../../store/thunks/voyagesThunks";
import { getContrastColor, getStatusColor } from "../../utils/format-helper";
import { incomingCallNestedPopperSx } from "../calls/incomingCalls/overlayPolicy";
import DateTimePickerInput from "../common/inputs/dateTimePicker";

import type { RootState } from "../../store/reducers/reducer";
import type { AppDispatch } from "../../store/store";
import type { PickList } from "../../utils/types/pickList";
import type {
  CreateTaskPayload,
  QuickTask,
  Task,
  TaskStatus,
  TaskType,
} from "../../utils/types/tasks";
import type { JobList } from "../../utils/types/voyages";
import type { SxProps, Theme } from "@mui/material/styles";

// Custom Popper and Paper for Autocomplete (matching portCallNotification).
// Elevated above the Incoming Calls overlay (see overlayPolicy.ts) so the
// dropdown list is visible/clickable when this drawer opens from that flow.
const CustomPopper = (props: any) => (
  <Popper {...props} placement="bottom-start" sx={incomingCallNestedPopperSx} />
);

const CustomPaper = styled(Paper)(({ theme: _theme }) => ({
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
  borderRadius: "8px",
  overflow: "hidden",
}));

interface TaskDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (task: CreateTaskPayload) => void;
  initialData?: Partial<Task>;
  predefinedTask?: { title: string; description: string } | null;
  isLoading?: boolean;
  defaultJobId?: string;
  bulk?: boolean;
  disableType?: boolean;
  sx?: SxProps<Theme>;
}

const DRAWER_WIDTH = 450;

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: "blocked", label: "Blocked" },
  { value: "todo", label: "To Do" },
  { value: "in-progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];
const TASK_TYPE: { value: TaskType; label: string }[] = [
  { value: "agent", label: "Agent" },
  { value: "husbandry", label: "Husbandry" },
];

// Internal form component - remounts when key changes to reset state
interface TaskFormInternalProps {
  onClose: () => void;
  onSubmit: (task: CreateTaskPayload) => void;
  initialData?: Partial<Task>;
  predefinedTask?: { title: string; description: string } | null;
  isLoading: boolean;
  defaultAgentId: string;
  defaultJobId?: string;
  bulk?: boolean;
  disableType?: boolean;
}

const TaskFormInternal: React.FC<TaskFormInternalProps> = ({
  onClose,
  onSubmit,
  initialData,
  predefinedTask,
  isLoading,
  defaultAgentId,
  defaultJobId,
  bulk = false,
  disableType = false
}) => {
  // Redux selectors
  const dispatch = useDispatch<AppDispatch>();
  const peopleList = useSelector(
    (state: RootState) => state.peopleList.peopleList
  );
  const voyageListData = useSelector(
    (state: RootState) => state.voyages.voyageListData
  );
  const voyagesLoading = useSelector(
    (state: RootState) => state.voyages.loading
  );
  const { pickList } = useSelector((state: RootState) => state.pickList);
  const { quickTask, quickTaskLoading } = useSelector(
    (state: RootState) => state.tasks
  );

  // Helper to format date for DateTime picker (ISO string)
  const formatDateForInput = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  };

  // Compute initial values once (component remounts when key changes)
  const initialValues = useMemo(() => {
    if (predefinedTask) {
      return {
        title: predefinedTask.title,
        description: predefinedTask.description,
        agentId: defaultAgentId,
        jobId: defaultJobId || "",
        dueDate: null,
        status: "todo" as TaskStatus,
        categories: [] as string[],
        type: "agent" as TaskType,
        comments: "",
      };
    }
    if (initialData) {
      return {
        title: initialData.title || "",
        description: initialData.description || "",
        agentId: initialData.agentId || "",
        jobId: initialData.jobId || "",
        dueDate: formatDateForInput(initialData.dueDate),
        status: initialData.status || ("todo" as TaskStatus),
        categories: initialData.categories || [],
        type: initialData.type || ("agent" as TaskType),
        comments: initialData.comments || "",
      };
    }
    return {
      title: "",
      description: "",
      agentId: defaultAgentId,
      jobId: defaultJobId || "",
      dueDate: "",
      status: "todo" as TaskStatus,
      categories: [],
      type: "agent" as TaskType,
      comments: "",
    };
  }, [predefinedTask, initialData, defaultAgentId, defaultJobId]);

  // Form state - initialized directly from computed values
  const [title, setTitle] = useState(initialValues.title);
  const [description, setDescription] = useState(initialValues.description);
  const [agentId, setAgentId] = useState(initialValues.agentId);
  const [jobId, setJobId] = useState(initialValues.jobId);
  const [dueDate, setDueDate] = useState(initialValues.dueDate);
  const [status, setStatus] = useState<TaskStatus>(initialValues.status);
  const [type, setType] = useState<TaskType>(initialValues.type);
  const [categories, setCategories] = useState<string[]>(
    initialValues.categories
  );
  const [comments, setComments] = useState(initialValues.comments);
  const [quickTaskAnchor, setQuickTaskAnchor] = useState<null | HTMLElement>(
    null
  );

  // Fetch pickList if not loaded
  useEffect(() => {
    if (pickList.length === 0) {
      dispatch(fetchPickList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch voyageListData if not loaded
  useEffect(() => {
    if (!voyageListData || voyageListData.length === 0) {
      dispatch(getVoyagesList({ version: "active" }));
    }
  }, [dispatch, voyageListData]);

  // Filter TMA-only agents for task assignment
  const tmaOnlyAgents = useMemo(
    () =>
      peopleList
        .filter(
          (agent) =>
            agent?.position?.value === "Agent" &&
            agent?.personId &&
            agent?.name &&
            !agent?.disabled &&
            agent?.company?.name === "Texas Marine Agency"
        )
        .sort((a, b) => a.name!.localeCompare(b.name!)),
    [peopleList]
  );

  // Find selected agent for Autocomplete value
  const selectedAgent = useMemo(
    () => tmaOnlyAgents.find((agent) => agent.personId === agentId) || null,
    [tmaOnlyAgents, agentId]
  );

  // Sort voyageListData by vessel name (matching portCallNotification)
  const sortedVoyageList = useMemo(
    () =>
      [...(voyageListData || [])].sort((a, b) => {
        const nameA = a?.vessel?.name || "";
        const nameB = b?.vessel?.name || "";
        if (!nameA && nameB) return 1;
        if (nameA && !nameB) return -1;
        return nameA.localeCompare(nameB);
      }),
    [voyageListData]
  );

  // Find selected job for Autocomplete value
  const selectedJob = useMemo(
    () => voyageListData?.find((job) => job.job_id === jobId) || null,
    [voyageListData, jobId]
  );

  // Filter status categories from pickList (type="status", parent_type="port_call")
  const statusCategories = useMemo(
    () =>
      pickList.filter(
        (item: PickList) =>
          item?.type === "status" && item.parent_type === "port_call"
      ),
    [pickList]
  );

  // Handlers
  const handleAgentChange = useCallback(
    (_event: any, value: (typeof tmaOnlyAgents)[0] | null) => {
      setAgentId(value?.personId || "");
    },
    []
  );

  const handleVesselChange = useCallback(
    (_event: any, value: JobList | null) => {
      setJobId(value?.job_id || "");
    },
    []
  );

  const handleDateTimeUpdate = useCallback(
    (_id: string, _field: string, value: string) => {
      setDueDate(value);
    },
    []
  );

  const handleQuickTaskSelect = useCallback((task: QuickTask) => {
    setTitle(task.title);
    setDescription(task.description);
    setQuickTaskAnchor(null);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!bulk && !title.trim()) return;

    onSubmit({
      title: title.trim(),
      description: description.trim(),
      agentId,
      dueDate,
      status,
      jobId: jobId || undefined,
      categories,
      type,
      comments: comments.trim(),
    });
  }, [
    title,
    description,
    agentId,
    dueDate,
    status,
    jobId,
    categories,
    type,
    comments,
    onSubmit,
    bulk
  ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">
          {initialData?.id
            ? "Edit Task"
            : bulk
            ? "Create New Tasks"
            : "Create New Task"}
        </Typography>
        {!bulk && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setQuickTaskAnchor(e.currentTarget)}
            >
              Quick Task
            </Button>
            <Menu
              anchorEl={quickTaskAnchor}
              open={Boolean(quickTaskAnchor)}
              onClose={() => setQuickTaskAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {quickTaskLoading ? (
                <MenuItem disabled>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 1,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                </MenuItem>
              ) : (
                quickTask.map((task) => (
                  <MenuItem
                    key={task.title}
                    onClick={() => handleQuickTaskSelect(task)}
                  >
                    {task.title}
                  </MenuItem>
                ))
              )}
            </Menu>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        <Stack spacing={2.5}>
          {/* Title */}
          {!bulk && (
            <>
              <Box>
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Title *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter task title"
                />
              </Box>

              {/* Description */}
              <Box>
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Description
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter task description"
                />
              </Box>
            </>
          )}

          {/* Agent */}
          <Box>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Agent
            </Typography>
            <Autocomplete
              disabled={bulk}
              options={tmaOnlyAgents}
              PopperComponent={CustomPopper}
              PaperComponent={CustomPaper}
              getOptionLabel={(option) => option?.name || "Unknown Agent"}
              value={selectedAgent}
              onChange={handleAgentChange}
              slotProps={{ popper: { sx: incomingCallNestedPopperSx } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Select TMA agent"
                />
              )}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option?.personId}
                  style={{
                    listStyleType: "none",
                    cursor: "pointer",
                    padding: "8px 16px",
                  }}
                >
                  <Typography variant="body2">
                    {option?.name || "Unknown Agent"}
                  </Typography>
                </li>
              )}
            />
          </Box>

          {/* Vessel */}
          <Box>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Vessel
            </Typography>
            <Autocomplete
              disabled={bulk}
              loading={voyagesLoading}
              options={sortedVoyageList}
              PopperComponent={CustomPopper}
              PaperComponent={CustomPaper}
              slotProps={{ popper: { sx: incomingCallNestedPopperSx } }}
              getOptionLabel={(option) => {
                const vesselName = option?.vessel?.name;
                const voyage = option?.voyage;
                if (!vesselName && !voyage) return "Unknown Vessel";
                if (!vesselName) return `Unknown Vessel v.${voyage}`;
                if (!voyage) return vesselName;
                return `${vesselName} v.${voyage}`;
              }}
              value={selectedJob}
              onChange={handleVesselChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Select vessel"
                />
              )}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option?.job_id}
                  style={{
                    listStyleType: "none",
                    cursor: "pointer",
                    padding: "0",
                  }}
                >
                  <Stack
                    direction="column"
                    width="100%"
                    sx={{
                      px: 2,
                      py: 1,
                      // "&:hover": { backgroundColor: "#d4e3f5" },
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">
                        {option?.vessel?.name || "Unknown Vessel"} v.
                        {option?.voyage}
                      </Typography>
                      <Chip
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(
                            option?.status?.value
                          ),
                          color: getContrastColor(
                            getStatusColor(option?.status?.value)
                          ),
                          marginLeft: 1,
                          fontSize: "0.7rem",
                          height: "20px",
                        }}
                        label={option?.status?.value || "Unknown Status"}
                      />
                    </Stack>
                    {option?.assigned_agent?.name && (
                      <Typography
                        variant="caption"
                        sx={{ fontStyle: "italic", color: "text.secondary" }}
                      >
                        Agent: {option?.assigned_agent?.name}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
            />
          </Box>

          {/* Due Date */}
          <Box>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Due Date
            </Typography>
            <DateTimePickerInput
              id="task-due-date"
              value={dueDate}
              field="dueDate"
              onCellUpdate={handleDateTimeUpdate}
              placeholder="Select date and time"
              showOutline
            />
          </Box>

          {/* Status */}
          <Box>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Status
            </Typography>
            <Autocomplete
              options={STATUS_OPTIONS}
              getOptionLabel={(option) => option.label}
              value={
                STATUS_OPTIONS.find((opt) => opt.value === status) ||
                STATUS_OPTIONS[1]
              }
              onChange={(_, newValue) => setStatus(newValue?.value || "todo")}
              disableClearable
              slotProps={{ popper: { sx: incomingCallNestedPopperSx } }}
              renderInput={(params) => <TextField {...params} size="small" />}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <Typography variant="body2">{option.label}</Typography>
                </li>
              )}
            />
          </Box>
          {/* Task type */}
          {!disableType && (
            <Box>
              <Typography variant="body2" fontWeight={500} mb={0.5}>
                Task Type
              </Typography>
              <Autocomplete
              options={TASK_TYPE}
              getOptionLabel={(option) => option.label}
              value={
                TASK_TYPE.find((opt) => opt.value === type) || TASK_TYPE[0]
              }
              onChange={(_, newValue) => setType(newValue?.value || "agent")}
              disableClearable
              slotProps={{ popper: { sx: incomingCallNestedPopperSx } }}
              renderInput={(params) => <TextField {...params} size="small" />}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <Typography variant="body2">{option.label}</Typography>
                </li>
              )}
            />
            </Box>
          )}

          {/* Comments */}
          {!bulk && (
            <Box>
              <Typography variant="body2" fontWeight={500} mb={0.5}>
                Comments
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                maxRows={10}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add comments or notes about what was completed..."
              />
            </Box>
          )}
        </Stack>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || (!bulk && !title.trim())}
        >
          {isLoading
            ? "Saving..."
            : initialData?.id
            ? "Update Task"
            : bulk
            ? "Create Tasks"
            : "Create Task"}
        </Button>
      </Box>
    </Box>
  );
};

// Wrapper component that manages the drawer and remounting the form
const TaskDrawer: React.FC<TaskDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  predefinedTask,
  isLoading = false,
  defaultJobId,
  bulk = false,
  disableType = false,
  sx,
}) => {
  const auth = useContext(AWSCognitoContext);

  // Generate a unique key based on the data to reset form state when drawer opens with different content
  const formKey = `${initialData?.id || "new"}-${
    predefinedTask?.title || ""
  }-${open}`;

  const handleClose = (
    _event: object,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    // Only close if it's not a backdrop click (only allow X button/Cancel button)
    if (reason !== "backdropClick") {
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: DRAWER_WIDTH },
      }}
      sx={sx}
    >
      {open && (
        <TaskFormInternal
          key={formKey}
          onClose={onClose}
          onSubmit={onSubmit}
          initialData={initialData}
          predefinedTask={predefinedTask}
          isLoading={isLoading}
          defaultAgentId={auth?.user?.person_id || ""}
          defaultJobId={defaultJobId}
          bulk={bulk}
          disableType={disableType}
        />
      )}
    </Drawer>
  );
};

export default TaskDrawer;
